import { RowData } from '@tanstack/react-table';
import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { useActiveFocusContext } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { PosVirtuosoTableRowProps } from 'src/tables/Table/PosVirtuosoTableComponent/PosVirtuosoTableContext';
import { Feature } from 'src/WebApiController';

import * as styles from '../Table.css';

export const PosVirtuosoTableRow = <T extends RowData>({
  context,
  children,
  ...props
}: PosVirtuosoTableRowProps<T>) => {
  const {
    tableRowStyle,
    tableRows,
    rowWrapper,
    RowComponent,
    onRowSelect,
    activeRowIds,
    onRowShiftSelect,
    rowSelectionV2,
  } = context || {};
  const inputRef = useRef<HTMLTableRowElement>(null);
  const { activeItem, onKeyDownHandler } = useActiveFocusContext();

  useEffect(() => {
    if (activeItem?.id && inputRef.current && activeItem.id === props.item.id) {
      inputRef.current.focus();
      inputRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
      inputRef.current.focus();
    }
  }, [activeItem, props.item.id]);
  const hasAccessibilityModeFeature = useUserHasFeature(
    Feature.AccessibilityMode
  );

  const isSelected = useMemo(() => {
    return rowSelectionV2
      ? Boolean(activeRowIds?.has(props.item.id))
      : props.item.getIsSelected();
  }, [activeRowIds, props.item, rowSelectionV2]);

  const rowProps = {
    className: clsx(styles.tableRow, {
      [styles.isSelectedRow]: isSelected,
      [styles.isSubRow]: props.item.depth > 0,
      [styles.adminHoldHighlight]: hasAccessibilityModeFeature,
    }),
    style: {
      ...tableRowStyle,
      width: '99%',
    },
    ref: inputRef,
    key: props.item.id,
    onClick: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
      e.stopPropagation();
      if (rowSelectionV2) {
        onRowSelect?.(tableRows, props.item, props['data-item-index']);
      } else {
        props.item.toggleSelected();
        onRowShiftSelect?.(tableRows, props.item, props['data-item-index']);
      }
    },
    onKeyDown:
      activeItem != null
        ? (e: React.KeyboardEvent<HTMLTableRowElement>) =>
            onKeyDownHandler(e, props['data-index'], tableRows)
        : undefined,
    tabIndex:
      activeItem != null
        ? props.item.getIsSelected() || props.item.id === activeItem?.id
          ? 0
          : -1
        : undefined,
    ...props,
  };

  children = rowWrapper
    ? rowWrapper(props.item.original, children, props['data-index'])
    : children;

  if (RowComponent) {
    return (
      <RowComponent row={props.item} {...rowProps}>
        {children}
      </RowComponent>
    );
  }

  return <tr {...rowProps}>{children}</tr>;
};
